import React from 'react';
import { Modal, Box, Typography, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import { useTranslation } from 'react-i18next';
import QuestionBankFormFields from './QuestionBankFormFields';
import { isQuestionFormValid } from './questionBankUtils';

export default function QuestionBankFormModal({
  open,
  editingQuestion,
  form,
  setForm,
  saving,
  onClose,
  onSave,
}) {
  const { t } = useTranslation();
  const isEdit = Boolean(editingQuestion);

  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 13000 }}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: 640 },
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          borderRadius: '20px',
          boxShadow: '0 25px 50px -12px rgba(14, 165, 233, 0.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.5,
            background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
            color: 'white',
            position: 'relative',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.35)',
              }}
            >
              <QuizOutlinedIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: 'white !important', lineHeight: 1.3 }}>
                {isEdit
                  ? t('questionBank.editQuestion', 'Edit Question')
                  : t('questionBank.addQuestion', 'Add Question')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.25, color: 'white !important', fontSize: '0.8rem' }}>
                {isEdit
                  ? t('questionBank.editSubtitle', 'Update this reusable interview question')
                  : t('questionBank.addSubtitle', 'Add a reusable question to your company bank')}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 3.5, overflowY: 'auto' }}>
          <QuestionBankFormFields form={form} setForm={setForm} />
        </Box>

        <Box
          sx={{
            px: 3.5,
            py: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.5,
            borderTop: '1px solid #f1f5f9',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 hover:border-gray-300 transition-all"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving || !isQuestionFormValid(form)}
            className="px-5 py-2.5 rounded-xl text-white font-semibold text-sm shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
          >
            {saving ? (
              <span className="inline-flex items-center gap-2">
                <CircularProgress size={14} sx={{ color: 'white' }} />
                {t('common.loading', 'Loading...')}
              </span>
            ) : (
              t('common.save', 'Save')
            )}
          </button>
        </Box>
      </Box>
    </Modal>
  );
}
